import React from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FormatProvider } from "@/contexts/FormatContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Pricing from "@/pages/Pricing";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import PowerRankings from "@/pages/PowerRankings";
import TradeCalculator from "@/pages/TradeCalculator";
import TradeFinder from "@/pages/TradeFinder";
import MarketTrends from "@/pages/MarketTrends";
import News from "@/pages/News";
import Referral from "@/pages/Referral";
import Compare from "@/pages/Compare";
import BillingSuccess from "@/pages/BillingSuccess";
import AuthCallback from "@/pages/AuthCallback";
import PlayerDetail from "@/pages/PlayerDetail";
import TradeAdvisor from "@/pages/TradeAdvisor";
import DraftBoard from "@/pages/DraftBoard";
import MockDraft from "@/pages/MockDraft";
import TradeHistory from "@/pages/TradeHistory";
import Portfolio from "@/pages/Portfolio";
import CommandCenter from "@/pages/CommandCenter";
import LeagueContext from "@/pages/LeagueContext";
import DevyScanner from "@/pages/DevyScanner";
import PickArbitrage from "@/pages/PickArbitrage";
import DmeValues from "@/pages/DmeValues";
import PublicPlayers from "@/pages/PublicPlayers";
import PublicPlayer from "@/pages/PublicPlayer";
import PublicRankings from "@/pages/PublicRankings";
import PublicTrade from "@/pages/PublicTrade";
import PublicPositionRankings from "@/pages/PublicPositionRankings";
import PublicCompare from "@/pages/PublicCompare";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Ledger from "@/pages/Ledger";
import ProGate from "@/components/ProGate";
import ContactWidget from "@/components/ContactWidget";
import { Toaster } from "sonner";

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/players" element={<PublicPlayers />} />
      <Route path="/players/:slug" element={<PublicPlayer />} />
      <Route path="/r/:shareId" element={<PublicRankings />} />
      <Route path="/v/:shareId" element={<PublicTrade />} />
      <Route path="/rankings/:format/:position" element={<PublicPositionRankings />} />
      <Route path="/compare/:slugA/vs/:slugB" element={<PublicCompare />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/app" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="home" element={<Home />} />
        <Route path="values" element={<DmeValues />} />
        <Route path="command" element={<ProGate feature="Command Center"><CommandCenter /></ProGate>} />
        <Route path="portfolio" element={<ProGate feature="Multi-League Portfolio"><Portfolio /></ProGate>} />
        <Route path="league-intel" element={<ProGate feature="DME League Intelligence"><LeagueContext /></ProGate>} />
        <Route path="leagues/:id/context" element={<ProGate feature="DME League Intelligence"><LeagueContext /></ProGate>} />
        <Route path="devy" element={<ProGate feature="The Prospect Pipeline"><DevyScanner /></ProGate>} />
        <Route path="pick-arbitrage" element={<ProGate feature="Pick Arbitrage"><PickArbitrage /></ProGate>} />
        <Route path="market-arbitrage" element={<Navigate to="/app/pick-arbitrage" replace />} />
        <Route path="ledger" element={<ProGate feature="The League Ledger"><Ledger /></ProGate>} />
        <Route path="power-rankings" element={<PowerRankings />} />
        <Route path="trade-calculator" element={<TradeCalculator />} />
        <Route path="compare" element={<Compare />} />
        <Route path="trade-finder" element={<ProGate feature="The Trade Finder"><TradeFinder /></ProGate>} />
        <Route path="advisor" element={<ProGate feature="The AI Trade Advisor"><TradeAdvisor /></ProGate>} />
        <Route path="draft" element={<ProGate feature="Rookie Draft & ADP"><DraftBoard /></ProGate>} />
        <Route path="mock-draft" element={<ProGate feature="The Mock Draft Simulator"><MockDraft /></ProGate>} />
        <Route path="trade-history" element={<ProGate feature="Trade History"><TradeHistory /></ProGate>} />
        <Route path="market" element={<MarketTrends />} />
        <Route path="news" element={<ProGate feature="Squad News"><News /></ProGate>} />
        <Route path="referral" element={<Referral />} />
        <Route path="players/:id" element={<PlayerDetail />} />
        <Route path="billing/success" element={<BillingSuccess />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <FormatProvider>
                <AppRouter />
                <ContactWidget />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    className: "de-toast",
                  }}
                />
              </FormatProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
